"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useEffect } from "react";
import { FiArrowRight, FiUser } from "react-icons/fi";
import Badge from "@/components/ui/badge";
import { getEventLogConfig } from "@/lib/event-log-config";
import { formatDateTime, formatStaffName, formatToHumanReadable } from "@/lib/format-data";
import { EventLogAction } from "@/types/enums";
import { eventLogQueryOptions } from "@/query/check-in/calendar/event-log-query";
type EventLogSurface = "block_dialog" | "appointment_sidebar";
interface EventLogHistoryProps {
  eventId: string;
  surface: EventLogSurface;
}

// Simple helper to strip enum prefixes and format properly
function formatStatus(status: string): string {
  // Remove "EventStatus." or similar prefixes, then format
  const cleanStatus = status.includes(".") ? status.split(".").pop() || status : status;
  return formatToHumanReadable(cleanStatus);
}

// Helper function to properly parse ISO datetime strings for event logs
function formatEventDateTime(isoString: string): string {
  try {
    // Parse the ISO string to a Date object, then format with time
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
      throw new Error("Invalid date");
    }
    return formatDateTime(date);
  } catch (error) {
    console.error("Error parsing event date:", isoString, error);
    return "Invalid Date";
  }
}

// Inline diff for a single time value. When old === new, the value renders
// once in muted text; when they differ, the old value is shown in red with a
// strikethrough next to the new value. Across a start–end range this puts the
// visual signal exactly on the endpoint that moved, so "which side changed"
// is answered by the formatting itself rather than by a label.
function TimeValueDiff(t0) {
  const $ = _c(15);
  const {
    oldIso,
    newIso
  } = t0;
  if (oldIso === newIso) {
    let t1;
    if ($[0] !== oldIso) {
      t1 = formatEventDateTime(oldIso);
      $[0] = oldIso;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <span>{t1}</span>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== oldIso) {
    t1 = formatEventDateTime(oldIso);
    $[4] = oldIso;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <span className="text-status-error-text/60 line-through">{t1}</span>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== newIso) {
    t3 = formatEventDateTime(newIso);
    $[8] = newIso;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <span>{t3}</span>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t4) {
    t5 = <span className="inline-flex flex-wrap items-baseline gap-1">{t2}{t4}</span>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
export default function EventLogHistory(t0) {
  const $ = _c(12);
  const {
    eventId,
    surface
  } = t0;
  let t1;
  if ($[0] !== eventId) {
    t1 = eventLogQueryOptions.list(eventId);
    $[0] = eventId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: logs,
    isLoading
  } = useQuery(t1);
  let t2;
  let t3;
  if ($[2] !== eventId || $[3] !== surface) {
    t2 = () => {
      posthog.capture("event_log_opened", {
        event_id: eventId,
        surface
      });
    };
    t3 = [eventId, surface];
    $[2] = eventId;
    $[3] = surface;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (isLoading) {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="space-y-4">{[1, 2, 3].map(_temp)}</div>;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  if (!logs || logs.length === 0) {
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="py-4 text-center text-body text-muted-foreground">No history available</div>;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t4;
  if ($[8] !== logs) {
    t4 = logs.map(_temp6);
    $[8] = logs;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <div className="space-y-3">{t4}</div>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function _temp6(log) {
  return <div key={log.id} className="space-y-1"><div className="flex items-center gap-2"><Badge className={getEventLogConfig(log.actionType).color}>{getEventLogConfig(log.actionType).label}</Badge><span className="text-caption text-muted-foreground">{formatDateTime(log.createdDate)}</span></div>{log.description && <p className="text-body text-foreground-secondary">“{log.description}”</p>}<div className="flex items-center gap-1 text-caption text-muted-foreground"><FiUser className="h-3 w-3" /><span>{formatStaffName(log.createdBy)}</span></div>{log.data && Object.keys(log.data).length > 0 && <div className="text-caption text-foreground-muted">{log.actionType === EventLogAction.STATUS_CHANGED && log.data.oldStatus && log.data.newStatus && <span className="flex items-center gap-1">{formatStatus(log.data.oldStatus)}<FiArrowRight className="h-3 w-3" />{formatStatus(log.data.newStatus)}</span>}{log.actionType === EventLogAction.RESCHEDULED && log.data.oldStart && log.data.newStart && (log.data.oldEnd && log.data.newEnd ? <span className="flex flex-wrap items-baseline gap-1"><TimeValueDiff oldIso={log.data.oldStart} newIso={log.data.newStart} /><span aria-hidden={true}>–</span><TimeValueDiff oldIso={log.data.oldEnd} newIso={log.data.newEnd} /></span> : <span className="flex items-center gap-1">{formatEventDateTime(log.data.oldStart)}<FiArrowRight className="h-3 w-3" />{formatEventDateTime(log.data.newStart)}</span>)}{(log.actionType === EventLogAction.COMPLETED || log.actionType === EventLogAction.NO_SHOW_MARKED) && log.data.oldStatus && <span className="flex items-center gap-1">{formatStatus(log.data.oldStatus)}<FiArrowRight className="h-3 w-3" />{log.actionType === EventLogAction.COMPLETED ? <span>{"Completed"}</span> : <span>{"No Show"}</span>}</span>}{log.actionType === EventLogAction.PROVIDER_CHANGED && <div className="space-y-0.5">{(log.data.removedUserIds as string[])?.map(_temp2)}{(log.data.addedUserIds as string[])?.map(_temp3)}</div>}{log.actionType === EventLogAction.PATIENT_CHANGED && <div className="space-y-0.5">{(log.data.removedUserIds as string[])?.map(_temp4)}{(log.data.addedUserIds as string[])?.map(_temp5)}</div>}{log.actionType === EventLogAction.SERVICE_ADDED && log.data.serviceTitle && <span className="flex items-center gap-1"><span className="text-status-success-text">+</span>{log.data.serviceTitle as string}</span>}{log.actionType === EventLogAction.SERVICE_REMOVED && log.data.serviceTitle && <span className="flex items-center gap-1"><span className="text-status-error-text">−</span>{log.data.serviceTitle as string}</span>}</div>}</div>;
}
function _temp5(id_2) {
  return <span key={id_2} className="flex items-center gap-1"><span className="text-status-success-text">+</span><span>{formatStaffName(id_2)}</span></span>;
}
function _temp4(id_1) {
  return <span key={id_1} className="flex items-center gap-1"><span className="text-status-error-text">−</span><span>{formatStaffName(id_1)}</span></span>;
}
function _temp3(id_0) {
  return <span key={id_0} className="flex items-center gap-1"><span className="text-status-success-text">+</span><span>{formatStaffName(id_0)}</span></span>;
}
function _temp2(id) {
  return <span key={id} className="flex items-center gap-1"><span className="text-status-error-text">−</span><span>{formatStaffName(id)}</span></span>;
}
function _temp(item) {
  return <div key={item} className="space-y-2"><div className="h-4 w-24 animate-pulse rounded bg-muted" /><div className="h-3 w-32 animate-pulse rounded bg-muted" /><div className="h-3 w-20 animate-pulse rounded bg-muted" /></div>;
}